import { X } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";

// Prototype toasts: dark pill at the bottom, 5 s (8 s with "Anulează"), polite live region.

type Undo = () => unknown | Promise<unknown>;
interface ToastState {
  id: number;
  text: string;
  undo?: Undo | undefined;
}

const Ctx = createContext<((text: string, undo?: Undo) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const counter = useRef(0);

  const show = useCallback((text: string, undo?: Undo) => {
    counter.current += 1;
    setToast({ id: counter.current, text, undo });
  }, []);

  useEffect(() => {
    if (!toast) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), toast.undo ? 8000 : 5000);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [toast]);

  async function doUndo() {
    const fn = toast?.undo;
    setToast(null);
    if (!fn) return;
    try {
      await fn();
      show("Modificarea a fost anulată.");
    } catch (err) {
      show(err instanceof Error ? err.message : "Nu am putut anula modificarea.");
    }
  }

  return (
    <Ctx.Provider value={show}>
      {children}
      <div className="toast-slot" role="status" aria-live="polite">
        {toast && (
          <div className="toast" key={toast.id}>
            <span style={{ flex: 1 }}>{toast.text}</span>
            {toast.undo && (
              <button type="button" className="undo" onClick={doUndo}>
                Anulează
              </button>
            )}
            <button
              type="button"
              className="x"
              aria-label="Închide notificarea"
              onClick={() => setToast(null)}
            >
              <X size={18} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast outside ToastProvider");
  return v;
}
